import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";

const DEFAULT_FIELDS = [
  ["name", "Nom"],
  ["company", "Entreprise"],
  ["title", "Poste"],
  ["email", "Email"],
  ["phone", "Téléphone"],
  ["address", "Adresse"],
  ["sector", "Secteur"],
  ["size", "Taille"],
  ["score", "Score IA"],
  ["status", "Statut"],
  ["source", "Source"],
  ["channel", "Canal"],
  ["first_contact", "Premier contact"],
  ["last_contact", "Dernier contact"],
  ["notes", "Notes"],
  ["interest_level", "Intérêt"],
  ["budget_mentioned", "Budget"],
  ["campaign_id", "Campagne"],
];

export default function ExportModal({ open, onClose, defaultFilters = {} }) {
  const [fields, setFields] = useState(DEFAULT_FIELDS.map(([k]) => k));
  const [format, setFormat] = useState("xlsx");
  const [minScore, setMinScore] = useState(0);
  const [loading, setLoading] = useState(false);

  const toggle = (k) => setFields((f) => (f.includes(k) ? f.filter((x) => x !== k) : [...f, k]));

  const run = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("prosperr_token");
      const res = await fetch(`${API_BASE}/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: "include",
        body: JSON.stringify({
          format, fields,
          filters: { ...defaultFilters, min_score: minScore },
        }),
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prospects.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
      onClose();
    } catch { toast.error("Erreur export"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Exporter mes leads</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Format</div>
            <div className="grid grid-cols-2 gap-2">
              {[["xlsx", "📊 Excel"], ["csv", "📄 CSV"]].map(([v, l]) => (
                <button key={v} onClick={() => setFormat(v)} data-testid={`fmt-${v}`} className={`rounded-2xl p-3 border text-sm font-semibold ${format === v ? "bg-emerald-500 text-white border-emerald-500" : "bg-[var(--surface)] text-zinc-300 border-[var(--border)]"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Score minimum : <strong>{minScore}</strong></div>
            <Slider min={0} max={100} step={5} value={[minScore]} onValueChange={(v) => setMinScore(v[0])} data-testid="export-score" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Champs</div>
            <div className="max-h-48 overflow-y-auto space-y-1.5 bg-[var(--surface-alt)] rounded-xl p-3">
              {DEFAULT_FIELDS.map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={fields.includes(k)} onCheckedChange={() => toggle(k)} />
                  <span className="text-sm text-zinc-300">{l}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Annuler</Button>
          <Button onClick={run} disabled={loading || fields.length === 0} className="rounded-full bg-emerald-500/100 hover:bg-emerald-500" data-testid="do-export">
            {loading ? "Export..." : "Télécharger"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
